import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { DirectLine, ConnectionStatus, Activity } from 'botframework-directlinejs'
import { useUtilsStore } from './UtilsContext'
import { useSpeechStore } from './SpeechContext'
import { useRetorikStore } from './RetorikContext'
import { useLocaleStore } from './LocaleContext'
import { useActivityStore } from './ActivityContext'
import { useViewStore } from './ViewContext'

import { LegacyDirectLineActivityType, LegacyDirectLineStatus } from '../../models/directLine'
import useActivityMiddleware from '../Middlewares/activityMiddleware'
import { RetorikActivity } from '../../models/activityTypes'
import type { DirectLineCreationData, DataAddedToChannelData } from '../../models/directLine'
import { Mode, RecognitionState } from '../../models/enums'
import { version } from '../../../package.json'

type DirectlineTimeExtension = {
  locale: string
  localTimezone: string
  localTimestamp: string
}

interface DirectlineState {
  directLine: DirectLine | null
  directLineStatus: ConnectionStatus
  connectionStatusSubscription: any
  activitySubscription: any
  userId: string
  externalActivityHandlers: Array<(action: any) => boolean>
  paginationToSend: number
  positionToSend:
    | {
        latitude: number
        longitude: number
      }
    | undefined
  activityWatermark: number
  conversationPaused: boolean
  actions: {
    patchActivityFromPart: (activity: Activity) => RetorikActivity
    checkIfHandlerAbortsActivity: (type: LegacyDirectLineActivityType, activity: Activity) => boolean
    dispatchStatus: (status: ConnectionStatus) => void
    dispatchActivity: (activity: Activity) => void
    subscribe: (value: DirectLine | null) => void
    unsubscribe: () => void
    end: () => void
    createDirectLine: (data: DirectLineCreationData) => void
    addDataInChannelData: () => DataAddedToChannelData
    checkMicrophoneAndCancel: () => void
    sendMessage: (text?: string, value?: Record<string, any>) => void
    sendEvent: (name: string, value?: Record<string, any> | null, callback?: any) => void
    sendTyping: () => void
    addExternalActivityHandler: (value: (action: any) => boolean) => void
    setExternalActivityHandlers: (value: Array<(action: any) => boolean>) => void
    setPaginationToSend: (value: number) => void
    setPositionToSend: (value: { latitude: number; longitude: number } | undefined) => void
    dispatchMarkActivity: (id: string, speaking: boolean) => void
    pauseConversation: () => void
    resumeConversation: () => void
    sendMessageToChatbot: (value: string) => void
    resetDirectlineStore: () => void
  }
}

const initialState = {
  directLine: null,
  directLineStatus: ConnectionStatus.Uninitialized,
  connectionStatusSubscription: null,
  activitySubscription: null,
  userId: '',
  externalActivityHandlers: [],
  paginationToSend: 10,
  positionToSend: undefined,
  activityWatermark: 0,
  conversationPaused: false
}

const postActivityTypes = [LegacyDirectLineActivityType.SEND_MESSAGE, LegacyDirectLineActivityType.SEND_EVENT]
const eventsWithoutCancel = ['setuser', 'davi.gdprconsent']

const pad = (num: number): string => {
  return (num < 10 ? '0' : '') + num
}

const getFormattedTimestamp = (date = new Date()): string => {
  const tzo = -date.getTimezoneOffset()
  const dif = tzo >= 0 ? '+' : '-'

  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    dif +
    pad(Math.floor(Math.abs(tzo) / 60)) +
    ':' +
    pad(Math.abs(tzo) % 60)
  )
}

const createDirectlineStore = (
  modeRef: React.RefObject<Mode>,
  recognitionStateRef: React.RefObject<RecognitionState>,
  retrievingConversationRef: React.RefObject<boolean>,
  conversationIdRef: React.RefObject<string | null>,
  isTactileRef: React.RefObject<boolean>,
  localeRef: React.RefObject<string>,
  sendBoxRefRef: React.RefObject<HTMLTextAreaElement | null>,
  channelDataRef: React.RefObject<Record<string, string | number | boolean | Record<string, any> | Array<string | number>> | undefined>,
  setIsWaitingForResponse: (value: boolean) => void,
  unlockClickedButton: () => void,
  addBotEvent: (value: RetorikActivity) => Promise<void>,
  addBotMessage: (value: RetorikActivity) => Promise<void>,
  addUserEvent: (value: RetorikActivity) => void,
  addUserMessage: (value: RetorikActivity) => Promise<void>,
  checkConversationId: (value: string) => void,
  setRetrievingConversation: (value: boolean) => void,
  checkDumpedLocaleFallback: () => string,
  cancelCurrentUtterance: () => void,
  checkActivityReplyToId: (replyToId?: string) => void,
  addSpeechRecognitionDynamicGrammar: (value: string | Array<string>) => void,
  toggleMicrophone: (fromExpectedInput?: boolean) => void
) => {
  return createStore<DirectlineState>((set, get) => {
    return {
      ...initialState,
      actions: {
        patchActivityFromPart: (activity: Activity): RetorikActivity => {
          const tempActivity = activity as RetorikActivity
          if (!activity.from) {
            tempActivity.from = { ...tempActivity.from, role: 'channel' }
          } else if (!activity.from.role) {
            if (activity.from.id === get().userId) {
              tempActivity.from = { ...tempActivity.from, role: 'user' }
            } else if (activity.from.id) {
              tempActivity.from = { ...tempActivity.from, role: 'bot' }
            } else {
              tempActivity.from = { ...tempActivity.from, role: 'channel' }
            }
          }

          return tempActivity
        },
        checkIfHandlerAbortsActivity: (type: LegacyDirectLineActivityType, activity: Activity): boolean => {
          const handlers = get().externalActivityHandlers

          if (handlers.length) {
            const action = {
              type: type,
              payload: {
                activity: activity
              }
            }

            for (let i = 0; i < handlers.length; i++) {
              const f = handlers[i]
              if (f(action)) {
                setIsWaitingForResponse(false)
                unlockClickedButton()
                console.log('Retorik Framework > aborted activity')
                return true
              }
            }
          }

          return false
        },
        dispatchStatus: (status: ConnectionStatus): void => {
          set({ directLineStatus: status })
          get().externalActivityHandlers.forEach((handler) => {
            handler({
              type: LegacyDirectLineStatus[status]
            })
            status === ConnectionStatus.Online &&
              handler({
                type: LegacyDirectLineActivityType.SET_LANGUAGE
              })
          })
        },
        dispatchActivity: (activity: Activity): void => {
          if (get().conversationPaused) {
            return
          }

          if (retrievingConversationRef.current) {
            // @ts-ignore
            const finalWatermark = get().directLine?.watermark
            if (!isNaN(parseInt(finalWatermark))) {
              const currentWatermark = get().activityWatermark + 1
              if (currentWatermark === parseInt(finalWatermark)) {
                setRetrievingConversation(false)
              } else {
                set({ activityWatermark: currentWatermark })
              }
            }
          }

          activity.conversation && checkConversationId(activity.conversation.id)
          // Patch 'from' part of the activity to distinct between 'bot', 'user and 'channel'
          const retorikActivity = useActivityMiddleware(get().actions.patchActivityFromPart(activity))

          let legacyType: LegacyDirectLineActivityType = LegacyDirectLineActivityType.UNKNOWN
          switch (retorikActivity.type) {
            case 'message':
              legacyType = retorikActivity.from.role === 'user' ? LegacyDirectLineActivityType.SEND_MESSAGE : LegacyDirectLineActivityType.INCOMING_ACTIVITY
              break
            case 'event':
              legacyType = retorikActivity.from.role === 'user' ? LegacyDirectLineActivityType.SEND_EVENT : LegacyDirectLineActivityType.INCOMING_ACTIVITY
              break
            case 'typing':
              legacyType = LegacyDirectLineActivityType.TYPING
              break
          }

          // Let speechStore check if this activity has an impact on a curent streaming
          checkActivityReplyToId(retorikActivity.replyToId)

          switch (legacyType) {
            case LegacyDirectLineActivityType.SEND_EVENT:
              addUserEvent(retorikActivity)
              break
            case LegacyDirectLineActivityType.SEND_MESSAGE:
              addUserMessage(retorikActivity)
              break
            case LegacyDirectLineActivityType.INCOMING_ACTIVITY: {
              if (!get().actions.checkIfHandlerAbortsActivity(legacyType, activity)) {
                // Add data for better speech recognition if therer are some
                if (retorikActivity.channelData?.speechRecognitionGrammars) {
                  addSpeechRecognitionDynamicGrammar(retorikActivity.channelData?.speechRecognitionGrammars)
                }

                if (retorikActivity.type === 'event') {
                  addBotEvent(retorikActivity)
                } else if (retorikActivity.type === 'message') {
                  addBotMessage(retorikActivity)
                }

                // Hide virtual keyboard if it is displayed (not working on safari and firefox)
                navigator.virtualKeyboard?.hide()
                if (isTactileRef.current) {
                  sendBoxRefRef.current?.blur()
                }
              }
              break
            }
          }

          // Send POST_ACTIVITY_FULFILLED if the legacy type is SEND_MESSAGE or SEND_EVENT
          if (postActivityTypes.includes(legacyType)) {
            get().actions.checkIfHandlerAbortsActivity(LegacyDirectLineActivityType.POST_ACTIVITY_FULFILLED, activity)
          }
        },
        subscribe: (value: DirectLine | null): void => {
          // Subscribe to connection status and activities if a value is provided
          const CSsubscription = value
            ? value.connectionStatus$.subscribe((newStatus) => {
                get().actions.dispatchStatus(newStatus)
              })
            : null
          const Asubscription = value
            ? value.activity$.subscribe((activity) => {
                activity.type !== 'typing' && get().actions.dispatchActivity(activity)
              })
            : null

          set({
            directLine: value,
            connectionStatusSubscription: CSsubscription,
            activitySubscription: Asubscription
          })
        },
        unsubscribe: (): void => {
          // Unsubscribe existing subscriptions to connection status and activities
          get().connectionStatusSubscription?.unsubscribe()
          get().activitySubscription?.unsubscribe()
        },
        end: (): void => {
          get().directLine?.end()
        },
        createDirectLine: (data: DirectLineCreationData): void => {
          // Check if there are handlers already set or in the data
          const currentHandlers = get().externalActivityHandlers
          const handlers = currentHandlers.length ? currentHandlers : data.externalActivityHandler ? [data.externalActivityHandler] : []

          // Send DIRECT_LINE/CONNECT before creating directline because if everything goes well it only sends DIRECT_LINE/CONNECT_FULFILLED
          handlers.forEach((handler) => {
            handler({
              type: LegacyDirectLineStatus[0]
            })
          })

          const directLine = new DirectLine({
            webSocket: true,
            ...data
          })
          get().actions.unsubscribe()
          get().actions.subscribe(directLine)
          set({
            userId: data.userId || 'userId',
            externalActivityHandlers: handlers
          })
        },
        addDataInChannelData: (): DataAddedToChannelData => {
          const dataAddedToChannelData: DataAddedToChannelData = {
            pageSize: get().paginationToSend || 10,
            version: version
          }

          const position = get().positionToSend

          if (position?.latitude && position.longitude) {
            dataAddedToChannelData.location = {
              latitude: position.latitude,
              longitude: position.longitude
            }
            dataAddedToChannelData.position = {
              latitude: position.latitude,
              longitude: position.longitude
            }
          }

          return channelDataRef.current ? { ...dataAddedToChannelData, ...channelDataRef.current } : dataAddedToChannelData
        },
        checkMicrophoneAndCancel: (): void => {
          if (modeRef.current === Mode.vocal && recognitionStateRef.current === RecognitionState.Listening) {
            toggleMicrophone()
          } else {
            cancelCurrentUtterance()
          }
        },
        sendMessage: (text?: string, value?: Record<string, any>): void => {
          if (get().conversationPaused) {
            return
          }

          // Check if we should cancel an utterance being played
          get().actions.checkMicrophoneAndCancel()

          const locale = checkDumpedLocaleFallback()
          const tempActivity: Activity & DirectlineTimeExtension = {
            from: {
              id: get().userId
            },
            type: 'message',
            channelData: get().actions.addDataInChannelData(),
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            localTimestamp: getFormattedTimestamp(),
            locale: locale
          }

          text && (tempActivity.text = text)
          value && (tempActivity.value = value)

          // Check if the activity should be stopped in the external activity handler if there is one
          if (get().actions.checkIfHandlerAbortsActivity(LegacyDirectLineActivityType.POST_ACTIVITY, tempActivity)) {
            return
          }
          if (get().actions.checkIfHandlerAbortsActivity(LegacyDirectLineActivityType.SEND_MESSAGE, tempActivity)) {
            return
          }

          get().directLine?.postActivity(tempActivity).subscribe()
        },
        sendEvent: (name: string, value?: Record<string, any> | null, callback?: any): void => {
          if (get().conversationPaused) {
            return
          }

          !name.toLowerCase().includes('davi.closewindow') && !eventsWithoutCancel.includes(name.toLowerCase()) && get().actions.checkMicrophoneAndCancel()

          const locale = checkDumpedLocaleFallback()

          const tempEvent: Activity & DirectlineTimeExtension = {
            from: {
              id: get().userId
            },
            type: 'event',
            channelData: get().actions.addDataInChannelData(),
            locale: locale,
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            localTimestamp: getFormattedTimestamp(),
            name: name,
            value: value
          }

          // Check if the event should be stopped in the external activity handler if there is one
          if (get().actions.checkIfHandlerAbortsActivity(LegacyDirectLineActivityType.POST_ACTIVITY, tempEvent)) {
            return
          }
          if (get().actions.checkIfHandlerAbortsActivity(LegacyDirectLineActivityType.SEND_EVENT, tempEvent)) {
            return
          }

          get()
            .directLine?.postActivity(tempEvent)
            .subscribe({
              error: (error: any) => console.log('Retorik Framework > error while sending event : ', error),
              complete: () => callback?.()
            })
        },
        sendTyping: (): void => {
          if (get().conversationPaused) {
            return
          }

          get()
            .directLine?.postActivity({
              from: {
                id: get().userId
              },
              type: 'typing'
            })
            .subscribe()
        },
        setPaginationToSend: (value: number): void => {
          set({ paginationToSend: value })
        },
        addExternalActivityHandler: (value: (action: any) => boolean): void => {
          set((state) => ({ externalActivityHandlers: [...state.externalActivityHandlers, value] }))
        },
        setExternalActivityHandlers: (value: Array<(action: any) => boolean>): void => {
          set({ externalActivityHandlers: value })
        },
        setPositionToSend: (value: { latitude: number; longitude: number } | undefined): void => {
          set({ positionToSend: value })
        },
        dispatchMarkActivity: (id: string, speaking: boolean): void => {
          const action = {
            type: LegacyDirectLineActivityType.MARK,
            payload: {
              activityID: id,
              name: 'speak',
              value: speaking
            }
          }
          get().externalActivityHandlers.forEach((handler) => {
            handler(action)
          })
        },
        pauseConversation: (): void => {
          set({ conversationPaused: true })
        },
        resumeConversation: (): void => {
          set({ conversationPaused: false })
        },
        sendMessageToChatbot: (value: string): void => {
          if (value && conversationIdRef.current && localeRef.current) {
            const tempActivity = {
              type: 'message' as 'message',
              conversation: {
                id: conversationIdRef.current
              },
              id: `fake-bot-${conversationIdRef.current}`,
              from: {
                id: 'fake-bot-id'
              },
              locale: localeRef.current,
              text: value,
              speak: value
            }

            get().actions.dispatchActivity(tempActivity)
          }
        },
        resetDirectlineStore: (): void => {
          get().actions.unsubscribe()
          get().actions.end()
          set({ ...initialState })
        }
      }
    }
  })
}

const DirectlineContext = createContext<StoreApi<DirectlineState> | null>(null)

const useDirectlineStore = <T,>(selector: (state: DirectlineState) => T): T => {
  const store = useContext(DirectlineContext)
  if (!store) throw new Error('Retorik Framework > useDirectlineStore must be used within DirectlineProvider')
  return useStore(store, selector)
}

const DirectlineProvider = ({ children }) => {
  const mode = useSpeechStore((state) => state.mode)
  const recognitionState = useSpeechStore((state) => state.activeRecognitionState)
  const retrievingConversation = useActivityStore((state) => state.retrievingConversation)
  const conversationId = useActivityStore((state) => state.conversationId)
  const isTactile = useViewStore((state) => state.isTactile)
  const locale = useLocaleStore((state) => state.locale)
  const sendBoxRef = useRetorikStore((state) => state.sendBoxRef)
  const channelData = useRetorikStore((state) => state.configuration.channelData)

  const modeRef = useRef<Mode>(Mode.text)
  const recognitionStateRef = useRef<RecognitionState>(RecognitionState.Closed)
  const retrievingConversationRef = useRef<boolean>(false)
  const conversationIdRef = useRef<string | null>(null)
  const isTactileRef = useRef<boolean>(false)
  const localeRef = useRef<string>('fr-FR')
  const sendBoxRefRef = useRef<HTMLTextAreaElement | null>(null)
  const channelDataRef = useRef<Record<string, string | number | boolean | Record<string, any> | Array<string | number>> | undefined>(undefined)

  const setIsWaitingForResponse = useRetorikStore((state) => state.actions.setIsWaitingForResponse)
  const unlockClickedButton = useUtilsStore((state) => state.actions.unlockClickedButton)
  const { addBotEvent, addBotMessage, addUserEvent, addUserMessage, checkConversationId, setRetrievingConversation } = useActivityStore((state) => state.actions)
  const checkDumpedLocaleFallback = useLocaleStore((state) => state.actions.checkDumpedLocaleFallback)
  const { cancelCurrentUtterance, checkActivityReplyToId, addSpeechRecognitionDynamicGrammar, toggleMicrophone } = useSpeechStore((state) => state.actions)

  const [store] = useState(() =>
    createDirectlineStore(
      modeRef,
      recognitionStateRef,
      retrievingConversationRef,
      conversationIdRef,
      isTactileRef,
      localeRef,
      sendBoxRefRef,
      channelDataRef,
      setIsWaitingForResponse,
      unlockClickedButton,
      addBotEvent,
      addBotMessage,
      addUserEvent,
      addUserMessage,
      checkConversationId,
      setRetrievingConversation,
      checkDumpedLocaleFallback,
      cancelCurrentUtterance,
      checkActivityReplyToId,
      addSpeechRecognitionDynamicGrammar,
      toggleMicrophone
    )
  )

  useEffect(() => {
    modeRef.current = mode
  }, [mode])

  useEffect(() => {
    recognitionStateRef.current = recognitionState
  }, [recognitionState])

  useEffect(() => {
    retrievingConversationRef.current = retrievingConversation
  }, [retrievingConversation])

  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  useEffect(() => {
    isTactileRef.current = isTactile
  }, [isTactile])

  useEffect(() => {
    localeRef.current = locale
  }, [locale])

  useEffect(() => {
    sendBoxRefRef.current = sendBoxRef
  }, [sendBoxRef])

  useEffect(() => {
    channelDataRef.current = channelData
  }, [channelData])

  return <DirectlineContext.Provider value={store}>{children}</DirectlineContext.Provider>
}

export default DirectlineProvider
export { useDirectlineStore }
